import { Auth0Provider, AppState } from '@auth0/auth0-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Auth0ProviderWithNavigateProps {
  children: React.ReactNode;
}

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const redirectUri = import.meta.env.VITE_AUTH0_CALLBACK_URL;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

export const Auth0ProviderWithNavigate = ({ children }: Auth0ProviderWithNavigateProps) => {
  const navigate = useNavigate();
  const missingConfig = [
    !domain ? 'VITE_AUTH0_DOMAIN' : null,
    !clientId ? 'VITE_AUTH0_CLIENT_ID' : null,
    !redirectUri ? 'VITE_AUTH0_CALLBACK_URL' : null,
    !audience ? 'VITE_AUTH0_AUDIENCE' : null
  ].filter((value): value is string => Boolean(value));

  const onRedirectCallback = (appState?: AppState) => {
    const returnTo = appState?.returnTo ?? '/';
    navigate(returnTo);
  };

  // The account-linking callback carries an Auth0 `code`/`state` from a
  // backend-driven authorize flow that auth0-react never initiated. Without
  // this, auth0-react tries to consume those params on load, fails to match a
  // transaction, and drops the SPA back to the SSO cookie (the freshly-linked
  // secondary identity). Skipping it keeps the SPA on its primary session.
  const skipRedirectCallback =
    typeof window !== 'undefined' && window.location.pathname === '/account-linking/callback';

  if (missingConfig.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-xl rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">Auth0 config missing</p>
          <p className="mt-1">
            Add these env vars and restart the dev server: {missingConfig.join(', ')}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={domain!}
      clientId={clientId!}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      authorizationParams={{
        audience: audience,
        redirect_uri: redirectUri,
        scope: 'openid profile email offline_access'
      }}
      skipRedirectCallback={skipRedirectCallback}
      onRedirectCallback={onRedirectCallback}>
      {children}
    </Auth0Provider>
  );
};
